import { useState, useEffect, useCallback, useRef } from "react";
import { googleAuth, refreshAuthToken, logout as logoutApi } from "@/api/client";
import { SERVICE_PAUSED } from "@/config/service";
import { ErrorCodes } from "@/api/types";
import type { AuthUser } from "@/api/types";

const STORAGE_KEY = "unlock-user";
const LAST_ACTIVE_KEY = "unlock-last-active";
const SESSION_CHECK_THRESHOLD = 5 * 60 * 1000; // 5 minutes
const SESSION_VALIDATED_KEY = "unlock-session-validated";

interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (idToken: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  sessionExpiredMessage: string | null;
  clearSessionExpiredMessage: () => void;
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState<string | null>(null);
  const isValidatingRef = useRef(false);

  // Clear session expired message
  const clearSessionExpiredMessage = useCallback(() => {
    setSessionExpiredMessage(null);
  }, []);

  // Update last active timestamp
  const updateLastActive = useCallback(() => {
    localStorage.setItem(LAST_ACTIVE_KEY, Date.now().toString());
  }, []);

  // Clear auth state (internal helper)
  const clearAuthState = useCallback(() => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LAST_ACTIVE_KEY);
    sessionStorage.removeItem(SESSION_VALIDATED_KEY);
  }, []);

  // Validate session by calling refresh API
  const validateSession = useCallback(async (showExpiredMessage = false): Promise<boolean> => {
    // Prevent concurrent validation
    if (isValidatingRef.current) return false;
    isValidatingRef.current = true;

    try {
      const response = await refreshAuthToken();

      if (response.code === 0) {
        updateLastActive();
        return true;
      }

      // Any non-success response means session is invalid - clear auth state
      clearAuthState();
      if (showExpiredMessage) {
        setSessionExpiredMessage("Session expired");
      }

      return false;
    } catch {
      // Network error or other failure - also clear auth state to be safe
      clearAuthState();
      return false;
    } finally {
      isValidatingRef.current = false;
    }
  }, [updateLastActive, clearAuthState]);

  // Keep a ref to validateSession to avoid re-running effect when it changes
  const validateSessionRef = useRef(validateSession);
  useEffect(() => {
    validateSessionRef.current = validateSession;
  }, [validateSession]);

  // Load stored user on mount and validate session (once per browser session)
  useEffect(() => {
    // Service paused: never touch the backend, always behave as signed out
    if (SERVICE_PAUSED) {
      setIsLoading(false);
      return;
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsedUser: AuthUser = JSON.parse(stored);
        setUser(parsedUser);

        // Only validate once per browser session to avoid excessive API calls
        const alreadyValidated = sessionStorage.getItem(SESSION_VALIDATED_KEY);
        if (alreadyValidated) {
          setIsLoading(false);
          return;
        }

        // Validate session on startup (silently)
        validateSessionRef.current(false).then((valid) => {
          if (valid) {
            // Mark as validated for this browser session
            sessionStorage.setItem(SESSION_VALIDATED_KEY, "true");
          }
          setIsLoading(false);
        });
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []); // Empty deps - only run on mount

  // Visibility change handler - check session when user returns to tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (SERVICE_PAUSED) return;

      if (document.visibilityState !== "visible") {
        // User left - record the time
        updateLastActive();
        return;
      }

      // User returned - check if we need to validate
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return; // Not logged in, nothing to check

      const lastActive = localStorage.getItem(LAST_ACTIVE_KEY);
      const lastActiveTime = lastActive ? parseInt(lastActive, 10) : 0;
      const timeSinceActive = Date.now() - lastActiveTime;

      // If user was away for more than threshold, validate session
      if (timeSinceActive > SESSION_CHECK_THRESHOLD) {
        validateSession(true); // Show message if expired
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Initialize last active time
    updateLastActive();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [validateSession, updateLastActive]);

  // Refresh token (called by API client on 401)
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const response = await refreshAuthToken();

      if (response.code === 0) {
        updateLastActive();
        return true;
      }

      // Refresh failed, clear auth state
      if (
        response.code === ErrorCodes.AUTH_TOKEN_EXPIRED ||
        response.code === ErrorCodes.AUTH_REFRESH_FAILED
      ) {
        clearAuthState();
      }

      return false;
    } catch {
      return false;
    }
  }, [updateLastActive, clearAuthState]);

  // Login with Google ID token
  const login = useCallback(async (idToken: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setSessionExpiredMessage(null); // Clear any expired message
      const response = await googleAuth(idToken);

      if (response.code === 0 && response.data) {
        const { user: authUser } = response.data;

        // Validate that we got a valid user object
        if (!authUser || !authUser.id || !authUser.email) {
          return false;
        }

        setUser(authUser);

        // Store only user info in localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(authUser));
        updateLastActive();
        // Mark session as validated after successful login
        sessionStorage.setItem(SESSION_VALIDATED_KEY, "true");

        return true;
      }

      return false;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [updateLastActive]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await logoutApi();
    } catch {
      // Ignore logout API errors
    }

    clearAuthState();
  }, [clearAuthState]);

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshToken,
    sessionExpiredMessage,
    clearSessionExpiredMessage,
  };
}
